import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, CircleCheck, CircleHelp, CircleX, Clock3, MessageCircle, Newspaper, RefreshCw, Scale, Smile } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { MinorDifferences } from "@/components/MinorDifferences";

export const KINDS = [
  { id: "fact", label: "Fact", icon: Newspaper, hint: "Something checkable about the world. Middle checks it against approved sources." },
  { id: "opinion", label: "Opinion", icon: MessageCircle, hint: "Your view, argument or prediction." },
  { id: "social", label: "Social", icon: Smile, hint: "Life updates, photos, chat and everyday moments." },
];

const KIND_CLS = { fact: "bg-emerald-50 text-emerald-800 border-emerald-200", opinion: "bg-sky-50 text-sky-800 border-sky-200", social: "bg-[#F5F1EB] text-slate-700 border-[#E7DFD3]" };

export const KindPicker = ({ value, onChange }) => (
  <div className="space-y-2" data-testid="post-kind-picker">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">What kind of post is this?</p>
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
      {KINDS.map(({ id, label, icon: Icon, hint }) => (
        <button key={id} type="button" role="radio" aria-checked={value === id} onClick={() => onChange(id)} data-testid={`post-kind-${id}`}
          className={`rounded-xl border p-3 text-left transition-colors ${value === id ? `${KIND_CLS[id]} ring-2 ring-offset-1 ring-slate-300` : "border-border bg-white hover:border-slate-300"}`}>
          <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={15} />{label}</span>
          <span className="mt-1 block text-xs leading-snug text-slate-500">{hint}</span>
        </button>
      ))}
    </div>
  </div>
);

const STATUS = {
  CHECKING: { icon: Clock3, cls: "bg-white text-slate-600 border-slate-200", text: "Being checked" },
  VERIFIED: { icon: CircleCheck, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  CORROBORATED: { icon: CircleCheck, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  "ONE SIDE ONLY": { icon: Scale, cls: "bg-amber-50 text-amber-800 border-amber-300" },
  DISPUTED: { icon: Scale, cls: "bg-amber-50 text-amber-800 border-amber-300" },
  "NOT CORROBORATED": { icon: CircleHelp, cls: "bg-stone-100 text-stone-700 border-stone-300" },
  CONTRADICTED: { icon: CircleX, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  NOT_CHECKABLE: { icon: CircleHelp, cls: "bg-stone-100 text-stone-700 border-stone-300" },
};

const Recheck = ({ p, setP }) => {
  const [busy, setBusy] = useState(false);
  if (!setP || ["CHECKING", "NOT_CHECKABLE"].includes(p.fact_status?.status)) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/posts/${p.id}/recheck`); setP((x) => ({ ...x, ...data })); toast.success("Middle is checking this fact again with the latest approved sources"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <Button size="sm" variant="outline" className="w-full rounded-full" disabled={busy} onClick={go} data-testid={`post-recheck-btn-${p.id}`}><RefreshCw size={13} className="mr-1.5" />Re-check this fact</Button>;
};

const statusText = (fs) => {
  if (fs.status === "CHECKING") return "Being checked";
  if (["VERIFIED", "CORROBORATED"].includes(fs.status)) return fs.sources ? `${fs.label} by ${fs.sources} approved source${fs.sources === 1 ? "" : "s"}` : `${fs.label} by approved sources`;
  if (fs.status === "CONTRADICTED") return "Contradicted by approved sources";
  if (fs.status === "NOT CORROBORATED") return "Not corroborated yet";
  return fs.label;
};

const FactStatus = ({ fs, id, p, setP }) => {
  const look = STATUS[fs.status] || STATUS.CHECKING;
  const Icon = look.icon;
  return (
    <Popover>
      <PopoverTrigger data-testid={`post-fact-status-${id}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${look.cls}`}>
        <Icon size={12} className={fs.status === "CHECKING" ? "animate-pulse" : ""} />{statusText(fs)}
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2 text-sm" data-testid={`post-fact-details-${id}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Middle fact check</p>
        {fs.status === "CHECKING" ? <p className="text-slate-600">Middle is checking this against its approved sources. The label updates when the check finishes.</p> : <>
          {fs.explanation && <p className="text-slate-700">{fs.explanation}</p>}
          <MinorDifferences items={fs.minor_differences} status={fs.status} testid={`post-minor-differences-${id}`} compact />
          {(fs.claims || []).map((c, i) => <p key={i} className="border-t border-border pt-2 text-xs text-slate-600">"{c.text}"</p>)}
          {fs.fact_id && <Link to={`/facts/${fs.fact_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--facts))] hover:underline" data-testid={`post-fact-link-${id}`}>See it in Facts, with sources<ArrowUpRight size={12} /></Link>}
        </>}
        <p className="text-[11px] text-slate-400">Checked only against Middle's approved sources.</p>
        <Recheck p={p} setP={setP} />
      </PopoverContent>
    </Popover>
  );
};

export const KindLine = ({ p, setP }) => {
  const kind = p.kind || "social";
  const k = KINDS.find((x) => x.id === kind);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`post-kind-line-${p.id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${KIND_CLS[kind]}`} data-testid={`post-kind-badge-${p.id}`}><k.icon size={12} />{k.label}</span>
      {kind === "fact" && p.fact_status && <FactStatus fs={p.fact_status} id={p.id} p={p} setP={setP} />}
      {p.fact_status?.status === "NOT_CHECKABLE" && <span className="text-[11px] text-slate-500" data-testid={`post-not-checkable-${p.id}`}>Only you can see this until you edit it into a checkable fact.</span>}
    </div>
  );
};

export const CautionNote = ({ p }) => p.caution?.concerns?.length ? (
  <div className="mt-3 flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid={`post-caution-${p.id}`}>
    <AlertTriangle size={15} className="shrink-0" />
    <p><b className="font-semibold">Caution: this may contain {p.caution.concerns.join(" or ")}.</b> {p.caution.reason} A Middle moderator is reviewing it.</p>
  </div>
) : null;
